import type { AppUpdateStatus, HttpTls, UpdateChannel } from './types.ts';

/**
 * Wire framing shared by all three shells: [type u8][channelId u32][length u32][payload].
 * Big-endian. One frame per message-channel message, so no stream reassembly is needed.
 *
 * Channel conversation (the app allocates channel ids; control payloads are UTF-8 JSON):
 *
 *   app   -> shell  Open      OpenRequest
 *   shell -> app    Response  (empty)            the channel is open
 *   shell -> app    Error     ErrorPayload       the open failed; the channel never existed
 *   both ways       Data      raw bytes
 *   shell -> app    Event     ChannelEvent       `written`: one app Data frame reached the socket
 *   app   -> shell  Close     (empty)            request to close
 *   shell -> app    Close     UTF-8 reason       closed; an empty payload means a clean close
 *
 * The shell answers every app Data frame with exactly one `written` event, in order. That is the
 * write back-pressure.
 *
 * A WebSocket is a channel too (`kind: 'ws'`). Each Data frame is one whole message, in both
 * directions: one byte `WsMessageKind`, then the message (UTF-8 for text). The Response that opens
 * it carries `WsOpenResponse`; the Close from the shell carries the close code and reason as text.
 *
 * Operations that are not channels (`storage.*`, `device.*`, `net.browse`) are requests:
 *
 *   app   -> shell  Request   BridgeRequest      on an id from the same counter as channels
 *   shell -> app    Response  result as JSON     see `BridgeResults`
 *   shell -> app    Error     ErrorPayload       refused or failed
 *
 * The shell sends events that belong to no channel on channel 0, which the app never allocates:
 *
 *   shell -> app    Event     HostEvent          `lifecycle`: the app went to the foreground or background; on
 *                                                iOS `foreground` also follows a spell of being only inactive
 *                                                `sensor`: a reading of a sensor the app started
 *
 * An HTTP request is a channel too (`kind: 'http'`), with the request body inside the open request:
 *
 *   app   -> shell  Open      OpenRequest        method, URL, headers, body, timeout
 *   shell -> app    Response  HttpResponseHead   status and headers have arrived
 *   shell -> app    Error     ErrorPayload       the request failed before a response arrived
 *   shell -> app    Data      raw bytes          the response body, in pieces
 *   shell -> app    Close     UTF-8 reason       empty: the body is complete; otherwise it was cut off
 *   app   -> shell  Close     (empty)            cancel the request
 *
 * The shell enforces `timeoutMs` for the whole exchange, body included, and follows redirects unless the
 * request says `redirect: 'manual'`: then a redirect is the response, also one to another scheme.
 * With `tls`, the shell trusts only its authorities and checks its name, and refuses the request when it
 * cannot; it never falls back to its usual trust.
 * Allowed difference: a response that stalls before the first bytes of its body may end in an Error
 * instead of Response + Close (iOS: URLSession holds the head back until some body has arrived).
 */
export const FRAME_HEADER_BYTES = 9;

/** Hardening: reject anything larger instead of allocating it. */
export const MAX_PAYLOAD_BYTES = 16 * 1024 * 1024;

export const FrameType = {
    Request: 1,
    Response: 2,
    Open: 3,
    Data: 4,
    Close: 5,
    Error: 6,
    Event: 7,
} as const;
export type FrameType = (typeof FrameType)[keyof typeof FrameType];

export interface Frame {
    type: FrameType;
    channelId: number;
    payload: Uint8Array;
}

/** Payload of an Open frame. */
export type OpenRequest = TcpOpenRequest | HttpOpenRequest | WsOpenRequest;

export interface WsOpenRequest {
    kind: 'ws';
    /** `ws:` or `wss:` only. */
    url: string;
    headers: Record<string, string>;
    protocols: string[];
}

/** Payload of the Response frame that opens a ws channel: the subprotocol the server chose, or ''. */
export interface WsOpenResponse {
    protocol: string;
}

/** First byte of every Data frame on a ws channel. */
export const WsMessageKind = {
    Text: 0,
    Binary: 1,
} as const;

/** Payload of a Request frame. Paths are relative to the app's storage root, see `isValidStoragePath`. */
export type BridgeRequest =
    | { op: 'storage.read'; path: string }
    | { op: 'storage.write'; path: string; data: string }
    | { op: 'storage.list'; prefix: string }
    | { op: 'storage.remove'; path: string }
    | { op: 'device.info' }
    | { op: 'device.keepAwake'; enabled: boolean }
    /** 0..1, or null: the system's brightness again. */
    | { op: 'device.setBrightness'; level: number | null }
    /** Starts or stops a sensor; its readings come as `sensor` host events. */
    | { op: 'device.watchSensor'; sensor: 'proximity' | 'light'; enabled: boolean }
    /** Looks for a service on the LAN by mDNS (DNS-SD) for `timeoutMs`, and answers what it found. */
    | { op: 'net.browse'; type: string; timeoutMs: number }
    /** The shell's updates of the app (app-updates AU-27). */
    | { op: 'app.updateStatus' }
    | { op: 'app.checkForUpdates' }
    | { op: 'app.applyUpdate' }
    | { op: 'app.ready' }
    | { op: 'app.setChannel'; channel: UpdateChannel };

/** Payload of the Response frame per operation. File contents are base64. */
export interface BridgeResults {
    'storage.read': { data: string | null };
    'storage.write': Record<string, never>;
    'storage.list': { paths: string[] };
    'storage.remove': Record<string, never>;
    'device.info': { platform: string; osVersion: string; model: string; brightness?: boolean; sensors?: ('proximity' | 'light')[] };
    'device.keepAwake': Record<string, never>;
    'device.setBrightness': Record<string, never>;
    'device.watchSensor': Record<string, never>;
    'net.browse': { services: BrowsedService[] };
    'app.updateStatus': AppUpdateStatus;
    'app.checkForUpdates': AppUpdateStatus;
    'app.applyUpdate': Record<string, never>;
    'app.ready': Record<string, never>;
    'app.setChannel': AppUpdateStatus;
}

/** A service found by `net.browse`. */
export interface BrowsedService {
    /** The instance's name, as people read it: "Hubitat Belgium", "p1meter-3E233E". */
    name: string;
    /** The host name it announces (`smile4a6e8b.local`). */
    host: string;
    port: number;
    /** IPv4 addresses first: a `.local` name does not resolve everywhere. */
    addresses: string[];
    /** The TXT record's values. */
    txt: Record<string, string>;
}

/** A DNS-SD service type: `_casa._tcp`. */
export const SERVICE_TYPE_PATTERN = /^_[A-Za-z0-9-]{1,63}\._(?:tcp|udp)$/;
/** How long `net.browse` may look, at most; services answer within a second or two. */
export const MAX_BROWSE_MS = 10_000;

/** Payload of an Event frame on channel 0. */
export type HostEvent = { type: 'lifecycle'; state: 'foreground' | 'background' };

/** Payload of an Event frame on channel 0: a reading of a sensor started with `device.watchSensor`; light at most a few times a second. */
export type SensorEvent = { type: 'sensor'; sensor: 'proximity'; near: boolean } | { type: 'sensor'; sensor: 'light'; lux: number };

/** The channel id of events that belong to no channel. */
export const HOST_EVENT_CHANNEL = 0;

/**
 * Storage paths: relative, `/`-separated, each segment letters, digits, `.`, `_` or `-`, not `.` or
 * `..`, at most 255 characters. Every host checks this before it touches the file system.
 */
export const STORAGE_PATH_PATTERN = /^(?!.*(?:^|\/)\.{1,2}(?:\/|$))[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/;

export function isValidStoragePath(path: string) : boolean {
    return path.length <= 255 && STORAGE_PATH_PATTERN.test(path);
}

/** A `storage.list` prefix: empty (everything), or a valid path, optionally ending in `/`. */
export function isValidStoragePrefix(prefix: string) : boolean {
    return prefix === '' || isValidStoragePath(prefix.endsWith('/') ? prefix.slice(0, -1) : prefix);
}

export interface TcpOpenRequest {
    kind: 'tcp';
    host: string;
    port: number;
}

export interface HttpOpenRequest {
    kind: 'http';
    method: string;
    /** `http:` or `https:` only. */
    url: string;
    headers: Record<string, string>;
    /** The request body as base64; absent when there is none. */
    body?: string;
    timeoutMs: number;
    /** `manual`: a redirect is the response, with its `location`, instead of being followed. Absent: followed. */
    redirect?: 'manual';
    /** `https:` only: the authorities and the name to trust instead of the system's (`HttpTls`). Absent: the system's. */
    tls?: HttpTls;
}

/** Payload of the Response frame that opens an http channel. Header names are lower case. */
export interface HttpResponseHead {
    status: number;
    headers: Record<string, string>;
}

/** Used when a request does not set `timeoutMs`. */
export const DEFAULT_HTTP_TIMEOUT_MS = 30_000;

/** Payload of an Event frame on an open channel. */
export type ChannelEvent = { type: 'written' };

/** Payload of an Error frame. */
export interface ErrorPayload {
    message: string;
}

export class FrameError extends Error {
    override name = 'FrameError';
}

const FRAME_TYPES: ReadonlySet<number> = new Set(Object.values(FrameType));
const MAX_UINT32 = 0xffff_ffff;

export function encodeFrame(frame: Frame) : Uint8Array {
    if (!FRAME_TYPES.has(frame.type)) {
        throw new FrameError(`unknown frame type ${frame.type}`);
    }
    if (!Number.isInteger(frame.channelId) || frame.channelId < 0 || frame.channelId > MAX_UINT32) {
        throw new FrameError(`invalid channel id ${frame.channelId}`);
    }
    if (frame.payload.length > MAX_PAYLOAD_BYTES) {
        throw new FrameError(`payload of ${frame.payload.length} bytes exceeds ${MAX_PAYLOAD_BYTES}`);
    }

    const out = new Uint8Array(FRAME_HEADER_BYTES + frame.payload.length);
    const view = new DataView(out.buffer);
    view.setUint8(0, frame.type);
    view.setUint32(1, frame.channelId);
    view.setUint32(5, frame.payload.length);
    out.set(frame.payload, FRAME_HEADER_BYTES);

    return out;
}

export function decodeFrame(bytes: Uint8Array) : Frame {
    if (bytes.length < FRAME_HEADER_BYTES) {
        throw new FrameError('frame shorter than header');
    }

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const type = view.getUint8(0);
    if (!FRAME_TYPES.has(type)) {
        throw new FrameError(`unknown frame type ${type}`);
    }

    const channelId = view.getUint32(1);
    const length = view.getUint32(5);
    if (length > MAX_PAYLOAD_BYTES) {
        throw new FrameError(`payload of ${length} bytes exceeds limit`);
    }
    if (bytes.length !== FRAME_HEADER_BYTES + length) {
        throw new FrameError(`length field ${length} does not match frame size ${bytes.length}`);
    }

    return { type: type as FrameType, channelId, payload: bytes.subarray(FRAME_HEADER_BYTES) };
}
